import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabState from '../../hooks/useTabState';
import { useApp } from '../../contexts/AppContext';
import api from '../../api';
import {
  UploadRecord,
  UploadStatus,
  MappingConfig,
  CoaAccount,
  TERMINAL_STATUSES,
} from './types';
import GLUploadTab from './GLUploadTab';
import CoAPanel from './CoAPanel';
import DataUploadTab from './DataUploadTab';

export default function GLUpload() {
  const { entities, hasPermission } = useApp();
  const navigate = useNavigate();

  // Tab
  const GL_TABS = ['gl', 'coa', 'data'] as const;
  const [activeTab, setActiveTab] = useTabState<typeof GL_TABS[number]>('tab', GL_TABS, 'gl');

  // GL upload form
  const [glEntity, setGlEntity] = useState('');
  const [glYear, setGlYear] = useState(new Date().getFullYear());
  const [glMonth, setGlMonth] = useState(new Date().getMonth() + 1);
  const [glMappingId, setGlMappingId] = useState<number | null>(null);
  const [glFile, setGlFile] = useState<File | null>(null);
  const [glReplaceExisting, setGlReplaceExisting] = useState(false);
  const [glUploading, setGlUploading] = useState(false);

  // Active upload tracking (polling)
  const [activeUpload, setActiveUpload] = useState<UploadStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Upload history
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);

  // Mappings
  const [mappings, setMappings] = useState<MappingConfig[]>([]);

  // CoA
  const [coaEntity, setCoaEntity] = useState('');
  const [coaFile, setCoaFile] = useState<File | null>(null);
  const [coaMappingId, setCoaMappingId] = useState<number | null>(null);
  const [coaReplaceExisting, setCoaReplaceExisting] = useState(false);
  const [coaUploading, setCoaUploading] = useState(false);
  const [coaResult, setCoaResult] = useState<any>(null);
  const [accounts, setAccounts] = useState<CoaAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // Data Upload
  const [dataEntity, setDataEntity] = useState('');
  const [dataType, setDataType] = useState('AUTO');
  const [erpType, setErpType] = useState('AUTO');
  const [dataFile, setDataFile] = useState<File | null>(null);
  const [dataUploading, setDataUploading] = useState(false);
  const [dataUploads, setDataUploads] = useState<any[]>([]);
  const [dataUploadsLoading, setDataUploadsLoading] = useState(false);
  const [dataDragOver, setDataDragOver] = useState(false);
  const dataInputRef = useRef<HTMLInputElement>(null);
  const dataPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeDataUploadId, setActiveDataUploadId] = useState<number | null>(null);

  // Entity Upload (D15)
  const [entityFile, setEntityFile] = useState<File | null>(null);
  const [entityUploading, setEntityUploading] = useState(false);
  const [entityResult, setEntityResult] = useState<any>(null);
  const entityInputRef = useRef<HTMLInputElement>(null);

  // Editing inline
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editType, setEditType] = useState('');

  // Toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // Delete upload
  const [deleteTarget, setDeleteTarget] = useState<UploadRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Rejected rows expansion
  const [showRejected, setShowRejected] = useState(false);

  // Drag and drop state
  const [glDragOver, setGlDragOver] = useState(false);
  const [coaDragOver, setCoaDragOver] = useState(false);
  const glInputRef = useRef<HTMLInputElement>(null);
  const coaInputRef = useRef<HTMLInputElement>(null);

  // Permission check
  const canUpload = hasPermission('upload_gl');

  // ===== Load data =====

  const loadUploads = useCallback(async () => {
    setUploadsLoading(true);
    try {
      const res = await api.get('/uploads', { params: { limit: 50 } });
      setUploads(res.data.uploads || []);
    } catch (e: any) {
      console.error('Failed to load uploads', e);
      showToast('Failed to load uploads. Please try again.', false);
    }
    setUploadsLoading(false);
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const res = await api.get('/uploads/mappings');
      setMappings(res.data.mappings || []);
    } catch (e: any) {
      console.error('Failed to load mappings', e);
      showToast('Failed to load mappings.', false);
    }
  }, []);

  const loadAccounts = useCallback(async (entityCode: string) => {
    if (!entityCode) {
      setAccounts([]);
      return;
    }
    setAccountsLoading(true);
    try {
      const res = await api.get(`/uploads/chart-of-accounts/${entityCode}`);
      setAccounts(res.data.accounts || []);
    } catch (e: any) {
      console.error('Failed to load accounts', e);
      showToast('Failed to load chart of accounts.', false);
    }
    setAccountsLoading(false);
  }, []);

  // Data Upload helpers
  const loadDataUploads = useCallback(async () => {
    setDataUploadsLoading(true);
    try {
      const res = await api.get('/data-uploads', { params: { limit: 50 } });
      setDataUploads(res.data.uploads || []);
    } catch (e: any) {
      console.error('Failed to load data uploads', e);
      showToast('Failed to load data uploads.', false);
    }
    setDataUploadsLoading(false);
  }, []);

  // D15: Entity Master Upload
  const handleEntityUpload = async () => {
    if (!entityFile) return;
    setEntityUploading(true);
    setEntityResult(null);
    const formData = new FormData();
    formData.append('file', entityFile);
    formData.append('org_id', '1');
    try {
      const res = await api.post('/entities/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEntityResult(res.data);
      setEntityFile(null);
      showToast(`Entity upload: ${res.data.created} created, ${res.data.updated} updated`, true);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Upload failed';
      showToast(msg, false);
      setEntityResult({ status: 'error', error: msg });
    } finally {
      setEntityUploading(false);
    }
  };

  const handleDataUpload = async () => {
    if (!dataFile) return;
    setDataUploading(true);

    const formData = new FormData();
    formData.append('file', dataFile);
    if (dataEntity) formData.append('entity_code', dataEntity);
    formData.append('org_id', '1');
    if (dataType !== 'AUTO') formData.append('data_type', dataType);
    if (erpType !== 'AUTO') formData.append('erp_type', erpType);

    try {
      const res = await api.post('/data-uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadId = res.data.upload_id;
      showToast(`Data upload received (${res.data.data_type}). Processing...`, true);
      setDataFile(null);
      setActiveDataUploadId(uploadId);

      // Poll for completion
      if (dataPollRef.current) clearInterval(dataPollRef.current);
      dataPollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/data-uploads/${uploadId}`);
          const st = statusRes.data;
          if (st.status === 'COMPLETE' || st.status === 'FAILED') {
            if (dataPollRef.current) clearInterval(dataPollRef.current);
            dataPollRef.current = null;
            setActiveDataUploadId(null);
            if (st.status === 'COMPLETE') {
              showToast(`Data upload complete: ${st.rows_ingested} rows ingested (${st.data_type})`, true);
            } else {
              showToast(`Data upload failed: ${st.status_detail || 'Unknown error'}`, false);
            }
            loadDataUploads();
          }
        } catch (err) {
          console.error('Data poll error', err);
        }
      }, 2000);
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'Data upload failed';
      showToast(msg, false);
    }
    setDataUploading(false);
  };

  const handleDeleteUpload = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/uploads/${deleteTarget.id}`);
      const d = res.data;
      showToast(`Deleted ${deleteTarget.filename} (${d.jes_deleted} JEs, ${d.exceptions_deleted} exceptions removed)`, true);
      setDeleteTarget(null);
      loadUploads();
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'Delete failed';
      showToast(msg, false);
    }
    setDeleting(false);
  };

  useEffect(() => { loadUploads(); loadMappings(); loadDataUploads(); }, [loadUploads, loadMappings, loadDataUploads]);

  useEffect(() => {
    if (coaEntity) loadAccounts(coaEntity);
  }, [coaEntity, loadAccounts]);

  // Auto-select first entity for GL/CoA tabs (required), NOT for Data Upload (optional)
  useEffect(() => {
    if (entities.length > 0 && !glEntity) {
      setGlEntity(entities[0].entity_code);
    }
    if (entities.length > 0 && !coaEntity) {
      setCoaEntity(entities[0].entity_code);
    }
    // Data Upload: leave blank — entity_code read from row data when available
  }, [entities, glEntity, coaEntity]);

  // ===== Polling =====

  const pollUploadStatus = useCallback(async (uploadId: number) => {
    try {
      const res = await api.get(`/uploads/${uploadId}`);
      const data: UploadStatus = res.data;
      setActiveUpload(data);

      if (TERMINAL_STATUSES.includes(data.status)) {
        // Stop polling
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        if (data.status === 'COMPLETE') {
          showToast(`Upload complete: ${data.rows_ingested} rows ingested, ${data.exceptions_detected || 0} exceptions detected`, true);
          loadUploads();
        } else if (data.status === 'FAILED') {
          showToast(`Upload failed: ${data.status_detail || 'Unknown error'}`, false);
          loadUploads();
        }
      }
    } catch (e: any) {
      console.error('Poll error', e);
    }
  }, [loadUploads]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (dataPollRef.current) clearInterval(dataPollRef.current);
    };
  }, []);

  // ===== GL Upload =====

  const handleGlUpload = async () => {
    if (!glFile || !glEntity) return;
    setGlUploading(true);
    setActiveUpload(null);
    setShowRejected(false);

    const formData = new FormData();
    formData.append('file', glFile);
    formData.append('entity_code', glEntity);
    formData.append('period_year', String(glYear));
    formData.append('period_month', String(glMonth));
    formData.append('org_id', '1');
    formData.append('replace_existing', String(glReplaceExisting));
    if (glMappingId) formData.append('mapping_id', String(glMappingId));

    try {
      const res = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadId = res.data.upload_id;
      showToast('Upload received, processing started...', true);

      // Start polling
      setActiveUpload({
        id: uploadId,
        entity_code: glEntity,
        period: `${glYear}-${String(glMonth).padStart(2, '0')}`,
        filename: glFile.name,
        status: 'UPLOADED',
        status_detail: null,
        row_count: null,
        rows_ingested: null,
        rows_rejected: null,
        exceptions_detected: null,
        jes_created: null,
        actions_created: null,
        uploaded_at: new Date().toISOString(),
        processing_started_at: null,
        processing_completed_at: null,
        uploaded_by: '',
        is_superseded: false,
        rejected_rows: [],
        processing_time_ms: null,
      });

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollUploadStatus(uploadId), 2000);
      // Also poll immediately
      setTimeout(() => pollUploadStatus(uploadId), 500);

      setGlFile(null);
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'Upload failed';
      showToast(msg, false);
    }
    setGlUploading(false);
  };

  // ===== CoA Upload =====

  const handleCoaUpload = async () => {
    if (!coaFile || !coaEntity) return;
    setCoaUploading(true);
    setCoaResult(null);

    const formData = new FormData();
    formData.append('file', coaFile);
    formData.append('entity_code', coaEntity);
    formData.append('org_id', '1');
    formData.append('replace_existing', String(coaReplaceExisting));
    if (coaMappingId) formData.append('mapping_id', String(coaMappingId));

    try {
      const res = await api.post('/uploads/chart-of-accounts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCoaResult(res.data);
      showToast(`CoA uploaded: ${res.data.accounts_created} created, ${res.data.accounts_updated} updated`, true);
      setCoaFile(null);
      loadAccounts(coaEntity);
    } catch (e: any) {
      const msg = e.response?.data?.detail || 'CoA upload failed';
      showToast(msg, false);
    }
    setCoaUploading(false);
  };

  // ===== Account inline edit =====

  const handleAccountTypeUpdate = async (accountCode: string, newType: string) => {
    try {
      await api.patch(`/uploads/chart-of-accounts/${coaEntity}/${accountCode}`, {
        account_type: newType,
      });
      showToast(`Account ${accountCode} updated to ${newType}`, true);
      setEditingCode(null);
      loadAccounts(coaEntity);
    } catch (e: any) {
      showToast('Update failed', false);
    }
  };

  // ===== Drag-and-drop helpers =====

  const handleDrag = (e: React.DragEvent, setOver: (v: boolean) => void, over: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setOver(true);
    else if (e.type === 'dragleave') setOver(false);
  };

  const handleDrop = (e: React.DragEvent, setFile: (f: File | null) => void, setOver: (v: boolean) => void) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  // ===== Render =====

  if (!canUpload) {
    return (
      <div style={{ padding: '40px 28px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        You do not have permission to access GL Upload. Required role: ADMIN, CFO, or CONTROLLER.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1440, margin: '0 auto' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8,
          background: toast.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.ok ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
          color: toast.ok ? 'var(--positive)' : '#fca5a5', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', maxWidth: 500,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 24 }}>
        {([
          { key: 'gl', label: 'GL Data' },
          { key: 'coa', label: 'Chart of Accounts' },
          { key: 'data', label: 'Data Upload' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key as any)} style={{
            padding: '10px 20px', fontSize: 13, fontWeight: activeTab === t.key ? 700 : 500,
            color: activeTab === t.key ? 'var(--navy-900)' : 'var(--text-secondary)',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === t.key ? '2px solid var(--navy-900)' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ============ GL DATA TAB ============ */}
      {activeTab === 'gl' && (
        <GLUploadTab
          entities={entities}
          glEntity={glEntity}
          setGlEntity={setGlEntity}
          glYear={glYear}
          setGlYear={setGlYear}
          glMonth={glMonth}
          setGlMonth={setGlMonth}
          glMappingId={glMappingId}
          setGlMappingId={setGlMappingId}
          glFile={glFile}
          setGlFile={setGlFile}
          glReplaceExisting={glReplaceExisting}
          setGlReplaceExisting={setGlReplaceExisting}
          glUploading={glUploading}
          glDragOver={glDragOver}
          setGlDragOver={setGlDragOver}
          glInputRef={glInputRef}
          mappings={mappings}
          handleGlUpload={handleGlUpload}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          activeUpload={activeUpload}
          showRejected={showRejected}
          setShowRejected={setShowRejected}
          setActiveUpload={setActiveUpload}
          uploads={uploads}
          uploadsLoading={uploadsLoading}
          deleteTarget={deleteTarget}
          setDeleteTarget={setDeleteTarget}
          deleting={deleting}
          handleDeleteUpload={handleDeleteUpload}
        />
      )}

      {/* ============ CHART OF ACCOUNTS TAB ============ */}
      {activeTab === 'coa' && (
        <CoAPanel
          entities={entities}
          coaEntity={coaEntity}
          setCoaEntity={setCoaEntity}
          coaFile={coaFile}
          setCoaFile={setCoaFile}
          coaMappingId={coaMappingId}
          setCoaMappingId={setCoaMappingId}
          coaReplaceExisting={coaReplaceExisting}
          setCoaReplaceExisting={setCoaReplaceExisting}
          coaUploading={coaUploading}
          coaResult={coaResult}
          coaDragOver={coaDragOver}
          setCoaDragOver={setCoaDragOver}
          coaInputRef={coaInputRef}
          mappings={mappings}
          accounts={accounts}
          accountsLoading={accountsLoading}
          editingCode={editingCode}
          setEditingCode={setEditingCode}
          editType={editType}
          setEditType={setEditType}
          handleCoaUpload={handleCoaUpload}
          handleAccountTypeUpdate={handleAccountTypeUpdate}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
        />
      )}

      {/* ============ DATA UPLOAD TAB ============ */}
      {activeTab === 'data' && (
        <DataUploadTab
          entities={entities}
          entityFile={entityFile}
          setEntityFile={setEntityFile}
          entityUploading={entityUploading}
          entityResult={entityResult}
          entityInputRef={entityInputRef}
          handleEntityUpload={handleEntityUpload}
          dataEntity={dataEntity}
          setDataEntity={setDataEntity}
          dataType={dataType}
          setDataType={setDataType}
          erpType={erpType}
          setErpType={setErpType}
          dataFile={dataFile}
          setDataFile={setDataFile}
          dataUploading={dataUploading}
          dataDragOver={dataDragOver}
          setDataDragOver={setDataDragOver}
          dataInputRef={dataInputRef}
          handleDataUpload={handleDataUpload}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          activeDataUploadId={activeDataUploadId}
          dataUploads={dataUploads}
          dataUploadsLoading={dataUploadsLoading}
        />
      )}
    </div>
  );
}
